import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "../Wahtsappstatus/Wahtsappstatus.css";
import { Upload, ImageIcon, Download, AlertCircle, Clock, CheckCircle2, XCircle } from 'lucide-react';

import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation } from "swiper/modules";
import axios from "axios";
import moment from "moment";
import APP_BASE_URL from "../../components/Api/globalApi";

import { imgBaseurl } from "../../baseUrl/baseurl.ts";

const Wahtsappstatus = () => {
  const storedUserId = localStorage.getItem("virtual_user_id") || localStorage.getItem("user_id");
  const storedToken = localStorage.getItem("virtualmall_token") || localStorage.getItem("token"); // Fallbacks for token
  const [status, setStatus] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${APP_BASE_URL}/virtualmall/watsappstatus-list`, {
        headers: { 
            user_id: storedUserId,
            Authorization: `Bearer ${storedToken}`
        },
      });
      if (response.data.result) {
          setStatus(response.data.list || []);
      }
    } catch (error) {
      console.error("Error fetching task list:", error);
      setError("Failed to fetch statuses. Please try again.");
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    return `${imgBaseurl}/${imagePath}`; 
  };

  const handleDownload = async (url, filename) => {
    try {
      const response = await axios.get(url, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "image/jpeg" });
      const objectUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      link.click();

      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Error downloading image:", error);
      setError("Failed to download the image. Please try again.");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileExt = file.name.split('.').pop().toLowerCase();
    if (fileExt === 'heic' || fileExt === 'heif') {
      setError("iOS HEIC/HEIF files are not supported by web browsers. Please convert your screenshot to JPG, JPEG, or PNG before uploading.");
      setSelectedFile(null);
      return;
    }

    setError("");
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError("Please select an image to upload.");
      return;
    }

    const fileExt = selectedFile.name.split('.').pop().toLowerCase();
    if (fileExt === 'heic' || fileExt === 'heif') {
      setError("iOS HEIC/HEIF files are not supported by web browsers. Please convert your screenshot to JPG, JPEG, or PNG before uploading.");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("image", selectedFile);

    try {
      const response = await axios.post(`${APP_BASE_URL}/virtualmall/watsappstatus-upload`, formData, {
        headers: { 
            user_id: storedUserId,
            Authorization: `Bearer ${storedToken}`
        },
      });
      
      if(response.data.result) {
          setSelectedFile(null);
          fetchStatus(); // Refresh status list after upload
          alert("WhatsApp Status uploaded successfully!");
      } else {
          setError(response.data.message || "Failed to upload");
      }
      setLoading(false);
    } catch (error) {
      console.error("Error uploading image:", error);
      setError("Failed to upload image. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="wahtsapp-status-container">
      <div className="status-upload-card">
        <h3>Upload New Status</h3>
        <div className="file-upload-wrapper">
          <div className="file-upload-box">
            <input
              type="file"
              accept="image/jpeg, image/jpg, image/png"
              onChange={handleFileChange}
            />
            <Upload className="upload-icon" />
            <div className="upload-text">Click or drag image to upload</div>
            <div className="upload-hint">Supported formats: JPG, PNG, JPEG</div>
          </div>
        </div>
        
        {selectedFile && (
          <div className="selected-file-info">
            <ImageIcon size={18} />
            <span>{selectedFile.name}</span>
          </div>
        )}

        {error && (
          <div className="text-danger mt-3 d-flex align-items-center justify-content-center gap-2">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        <button
          onClick={() => handleUpload()}
          disabled={!selectedFile || loading}
          className="status-upload-btn"
        >
          {loading ? (
            <div className="spinner-border spinner-border-sm text-light" role="status">
              <span className="visually-hidden">Uploading...</span>
            </div>
          ) : (
            "Upload Status"
          )}
        </button>
      </div>

      <div className="status-viewer-section">
        <h3>Previous Uploads</h3>
        {status?.length > 0 ? (
          <Swiper
            pagination={{ clickable: true }}
            navigation
            modules={[Pagination, Navigation]}
          >
            {status?.map((task) => (
              <SwiperSlide key={task.id}>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.8 }}
                  className="status-image-container"
                >
                  <img
                    src={getImageUrl(task.image_path)}
                    alt={task.created_at}
                  />
                  {/* Floating Status Badge */}
                  <div className={`status-badge-overlay status-badge-${task.status || 'pending'}`}>
                    {task.status === 'approved' && <CheckCircle2 size={14} />}
                    {task.status === 'rejected' && <XCircle size={14} />}
                    {(task.status === 'pending' || !task.status) && <Clock size={14} />}
                    <span>
                      {task.status === 'approved'
                        ? `Approved ${task.points ? `(+${task.points} pts)` : ''}`
                        : (task.status || 'pending')}
                    </span>
                  </div>
                  <div className="status-overlay-info">
                    <div className="status-date">
                      {moment(task.created_at).format("MMM DD, YYYY")}
                    </div>
                  </div>
                  <button
                    onClick={() =>
                      handleDownload(
                        getImageUrl(task.image_path),
                        `status-${task.id}.jpg`
                      )
                    }
                    className="status-download-btn"
                    title="Download Image"
                  >
                    <Download size={20} />
                  </button>
                </motion.div>
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <div className="empty-status-state">
            <ImageIcon className="empty-status-icon" />
            <h5>No statuses available</h5>
            <p>Upload a new image above to add your first status.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Wahtsappstatus;
